import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import MarkdownBody from "@/components/MarkdownBody";
import { API_BASE_URL } from "@/lib/apiBase";
import { toastError } from "@/lib/toast";
import {
  convertCsvToSheets,
  convertDocToHtml,
  convertDocxToHtml,
  convertXlsxToSheets,
  isCsvMime,
  isDocMime,
  isDocxMime,
  isOfficePreviewMime,
  isSpreadsheetMime,
} from "@/lib/officePreview";

function fileDisplayName(item) {
  return item?.name || item?.file_name || "file";
}

function contentRequest(item, teamId, orgId, disposition) {
  if (item?.node_id != null && teamId) {
    return {
      url: `${API_BASE_URL}/team/${teamId}/files/${item.node_id}/content`,
      params: { org_id: orgId, disposition },
    };
  }
  if (item?.url) {
    return {
      url: `${API_BASE_URL}${item.url}`,
      params: orgId ? { org_id: orgId } : undefined,
    };
  }
  return null;
}

/**
 * Preview / download a Team Files node or a workplace DM media blob.
 */
export default function TeamFilePreviewDialog({
  open,
  onOpenChange,
  orgId,
  teamId,
  item,
  accessToken,
}) {
  const [previewText, setPreviewText] = useState(null);
  const [previewHtml, setPreviewHtml] = useState(null);
  const [previewSheets, setPreviewSheets] = useState(null);
  const [previewSheetIndex, setPreviewSheetIndex] = useState(0);
  const [previewUrl, setPreviewUrl] = useState(null);
  const previewUrlRef = useRef(null);
  const displayName = fileDisplayName(item);

  const authHeaders = accessToken
    ? { Authorization: `Bearer ${accessToken}` }
    : {};

  const clearPreviewMedia = () => {
    setPreviewText(null);
    setPreviewHtml(null);
    setPreviewSheets(null);
    setPreviewSheetIndex(0);
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
    }
    setPreviewUrl(null);
  };

  const handleDownload = async () => {
    const req = contentRequest(item, teamId, orgId, "attachment");
    if (!req) return;
    try {
      const res = await axios.get(req.url, {
        headers: authHeaders,
        params: req.params,
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = displayName;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toastError(err, "Download failed");
    }
  };

  useEffect(() => {
    const req = contentRequest(item, teamId, orgId, "inline");
    if (!open || !req || !accessToken) {
      clearPreviewMedia();
      return undefined;
    }
    if (!item.previewable) {
      handleDownload();
      onOpenChange?.(false);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      clearPreviewMedia();
      try {
        const res = await axios.get(req.url, {
          headers: authHeaders,
          params: req.params,
          responseType: "blob",
        });
        if (cancelled) return;
        const mime = item.mime_type || "";
        const isText =
          (mime.startsWith("text/") ||
            mime === "text/csv" ||
            mime === "application/csv") &&
          !isCsvMime(mime);
        if (isOfficePreviewMime(mime)) {
          try {
            const arrayBuffer = await res.data.arrayBuffer();
            if (isDocMime(mime)) {
              setPreviewHtml(await convertDocToHtml(arrayBuffer));
            } else if (isDocxMime(mime)) {
              setPreviewHtml(await convertDocxToHtml(arrayBuffer));
            } else if (isSpreadsheetMime(mime)) {
              const sheets = await convertXlsxToSheets(arrayBuffer);
              setPreviewSheets(sheets);
              setPreviewSheetIndex(0);
            }
          } catch (convertErr) {
            toastError(convertErr, "Could not preview file — downloading instead");
            onOpenChange?.(false);
            await handleDownload();
          }
        } else if (
          isCsvMime(mime) ||
          displayName.toLowerCase().endsWith(".csv")
        ) {
          const text = await res.data.text();
          try {
            const sheets = convertCsvToSheets(text);
            setPreviewSheets(sheets);
            setPreviewSheetIndex(0);
          } catch {
            setPreviewText(text);
          }
        } else if (isText) {
          setPreviewText(await res.data.text());
        } else {
          const url = URL.createObjectURL(res.data);
          previewUrlRef.current = url;
          setPreviewUrl(url);
        }
      } catch (err) {
        if (!cancelled) {
          toastError(err, "Preview failed");
          onOpenChange?.(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, item?.node_id, item?.url, teamId, orgId, accessToken]);

  const previewIsMarkdown = (() => {
    if (!item) return false;
    const mime = (item.mime_type || "").split(";")[0].trim().toLowerCase();
    const name = displayName.toLowerCase();
    return mime === "text/markdown" || name.endsWith(".md");
  })();

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) clearPreviewMedia();
        onOpenChange?.(o);
      }}
    >
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="truncate pr-8">
            {item ? displayName : "Preview"}
          </DialogTitle>
        </DialogHeader>
        {previewSheets?.length > 1 && (
          <div className="flex flex-wrap gap-1 shrink-0">
            {previewSheets.map((sheet, idx) => (
              <Button
                key={`${sheet.name}-${idx}`}
                type="button"
                size="sm"
                variant={idx === previewSheetIndex ? "default" : "outline"}
                onClick={() => setPreviewSheetIndex(idx)}
              >
                {sheet.name}
              </Button>
            ))}
          </div>
        )}
        <div className="flex-1 min-h-0 overflow-auto rounded-md border border-border bg-muted/20 p-2">
          {previewText != null ? (
            previewIsMarkdown ? (
              <div className="p-3">
                <MarkdownBody content={previewText} />
              </div>
            ) : (
              <pre className="text-xs whitespace-pre-wrap font-mono p-2">
                {previewText}
              </pre>
            )
          ) : previewHtml != null ? (
            <div
              className="office-preview prose prose-sm dark:prose-invert max-w-none p-3 [&_table]:w-full [&_table]:border-collapse [&_td]:border [&_td]:border-border [&_td]:px-2 [&_td]:py-1 [&_th]:border [&_th]:border-border [&_th]:px-2 [&_th]:py-1 [&_th]:bg-muted/40"
              dangerouslySetInnerHTML={{ __html: previewHtml }}
            />
          ) : previewSheets != null ? (
            previewSheets.length === 0 ? (
              <p className="text-sm text-muted-foreground p-4">
                This workbook has no sheets.
              </p>
            ) : (
              <div
                className="office-preview prose prose-sm dark:prose-invert max-w-none p-3 overflow-x-auto [&_table]:w-full [&_table]:border-collapse [&_td]:border [&_td]:border-border [&_td]:px-2 [&_td]:py-1 [&_th]:border [&_th]:border-border [&_th]:px-2 [&_th]:py-1 [&_th]:bg-muted/40"
                dangerouslySetInnerHTML={{
                  __html:
                    previewSheets[previewSheetIndex]?.html ||
                    previewSheets[0]?.html ||
                    "",
                }}
              />
            )
          ) : previewUrl && (item?.mime_type || "").startsWith("image/") ? (
            <img
              src={previewUrl}
              alt={displayName}
              className="max-w-full max-h-[60vh] mx-auto object-contain"
            />
          ) : previewUrl && item?.mime_type === "application/pdf" ? (
            <iframe
              title={displayName}
              src={previewUrl}
              className="w-full h-[60vh] rounded"
            />
          ) : (
            <p className="text-sm text-muted-foreground p-4">Loading…</p>
          )}
        </div>
        {item && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="self-start"
            onClick={handleDownload}
          >
            <Download className="h-3.5 w-3.5 mr-1" />
            Download
          </Button>
        )}
      </DialogContent>
    </Dialog>
  );
}
